import React, { FC, useState } from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import Markdown from "markdown-to-jsx"

import { LpHeroFragmentFragment } from "@graphqlTypes"

import { generateConditionalClassName } from "@utils"

import Button, {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@components/Button/Button"
import HubspotForm from "@components/HubspotForm/HubspotForm"
import Media from "@components/lp/Media/Media"
import Hero from "@components/Hero/Hero"
import Modal from "@components/Modal/Modal"

import LpSectionTitle from "@components/lp/LpSection/components/LpSectionTitle/LpSectionTitle"

import DemoSchedulerForm from "./components/DemoSchedulerForm/DemoSchedulerForm"
import ReferralForm from "./components/ReferralForm/ReferralForm"

import "./LpHero.scss"
import { Icon, ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

export enum LP_HERO_VARIANTS {
  DEFAULT = "DEFAULT",
  HORIZONTAL = "HORIZONTAL",
  VERTICAL = "VERTICAL",
  FULL_BLEED = "FULL_BLEED",
}

export enum LP_HERO_CTA_VARIANTS {
  BUTTON = "BUTTON",
  FORM = "FORM",
}

export interface LpHeroCTA {
  label?: string
  url?: string
  onClick?: () => void,
  variant: LP_HERO_CTA_VARIANTS
}

const LpHero: FC<LpHeroFragmentFragment> = ({
  backgroundColor,
  backgroundImage,
  bodyText,
  button1Label,
  button1Url,
  button2Label,
  button2Url,
  contentful_id,
  form,
  formGatedPdfLink,
  hasSplitBackground,
  hubspotFormData,
  image,
  includeBottomOverlap,
  includeMediaBoxShadow,
  marginBottom,
  openFormInModal,
  overline,
  overlineIcon,
  overlineLinkUrl,
  playVideoInPnpPlayer,
  playVideoInline,
  title,
  useDisplayFont,
  variant,
  wistiaId,
}) => {
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false)

  // /lp/get-a-demo/ specific form implementation
  const hasDemoSchedulerForm = form === "DEMO_SCHEDULER"

  // /referral/ specific form implementation
  const hasReferralForm = form === "REFERRAL"

  // Generate array of hero CTAs depending on whether or not these values are populated in Contentful
  const generateOptionalHeroCtas = ({
    button1Label,
    button1Url,
    button2Label,
    button2Url,
    hubspotFormData,
  }: {
    button1Label: LpHeroFragmentFragment["button1Label"]
    button1Url: LpHeroFragmentFragment["button1Url"]
    button2Label: LpHeroFragmentFragment["button2Label"]
    button2Url: LpHeroFragmentFragment["button2Url"]
    hubspotFormData: LpHeroFragmentFragment["hubspotFormData"]
  }) => {
    const heroCtaList: LpHeroCTA[] = []
    // Form CTAs cannot appear with CTA buttons.
    // If a form CTA is populated, ignore any primary/secondary CTA buttons which may be populated
    if ((hubspotFormData || hasDemoSchedulerForm || hasReferralForm) && !openFormInModal) {
      heroCtaList.unshift({
        label: "",
        variant: LP_HERO_CTA_VARIANTS.FORM,
      })
      return heroCtaList
    }

    if (button2Label && button2Url)
      heroCtaList.unshift({
        label: button2Label,
        url: button2Url,
        variant: LP_HERO_CTA_VARIANTS.BUTTON,
      })

    // make sure button1 gets added to the beginning of the array
    if (button1Label && (button1Url || openFormInModal))
      heroCtaList.unshift({
        label: button1Label,
        url: openFormInModal ? "" : button1Url || "#",
        onClick: openFormInModal ? () => setIsFormModalOpen(true) : undefined,
        variant: LP_HERO_CTA_VARIANTS.BUTTON,
      })

    return heroCtaList
  }

  const optionalCtas = generateOptionalHeroCtas({
    button1Label,
    button1Url,
    button2Label,
    button2Url,
    hubspotFormData,
  })

  // contentful variants are: DEFAULT (horizontal), HORIZONTAL, VERTICAL;
  // inferred variant (based on truthiness of image && backgroundImage fields) is FULL_BLEED
  const _variant =
    ((!image && !wistiaId) || variant === LP_HERO_VARIANTS.VERTICAL) // if there is no image/media, use Vertical layout
      ? LP_HERO_VARIANTS.VERTICAL
      : (!!(image && backgroundImage) || variant === LP_HERO_VARIANTS.FULL_BLEED)
        ? LP_HERO_VARIANTS.FULL_BLEED
        : LP_HERO_VARIANTS.HORIZONTAL

  return (
    <Hero
      className={generateConditionalClassName({
        LpHero: true,
        "split-background": _variant === LP_HERO_VARIANTS.VERTICAL && hasSplitBackground === true,
        "full-bleed": _variant === LP_HERO_VARIANTS.FULL_BLEED,
        [`margin-bottom--${marginBottom || "XL"}`]: true,
        "include-bottom-overlap": !!includeBottomOverlap,
        [`include-bottom-overlap--${includeBottomOverlap}`]: !!includeBottomOverlap && includeBottomOverlap !== "None",
        [`variant--${variant}`]: true,
        [`uid--${contentful_id}`]: true,
      })}
      style={{
        "--LpHero--background": backgroundColor ? backgroundColor : "var(--color--clay-100)",
      }}
    >
      <div className="container">
        <div className="grid">
          <div
            className={generateConditionalClassName({
              "LpHero--heading-wrapper": true,
              "column column--2-12-M":
                _variant === LP_HERO_VARIANTS.VERTICAL,
              "column column--1-11-S column--1-9-M column--1-7-L":
                _variant === LP_HERO_VARIANTS.HORIZONTAL,
              "column column--1-6-S column--1-8-M column--1-6-L":
                _variant === LP_HERO_VARIANTS.FULL_BLEED,
            })}
          >
            {overline && (
              <p className="LpHero--overline margin-bottom--S type--mono-S color--text-light">
                {overlineLinkUrl ? (
                  <>
                    <a href={overlineLinkUrl}>
                      {overlineIcon && <Icon color="var(--color--text-light)" size="1.25em" type={overlineIcon as ICON_TYPES} />}
                      {overline.split(" / ")[0]}
                    </a>
                    {overline.split(" / ")[1] && <span style={{ marginLeft: "0.25em" }}>{` / ${overline.split(" / ")[1]}`}</span>}
                  </>
                ) : (
                  <>
                    {overlineIcon && <Icon color="var(--color--text-light)" size="1.25em" type={overlineIcon as ICON_TYPES} />}
                    {overline}
                  </>
                )}
              </p>
            )}
            {title && (
              <LpSectionTitle
                className={generateConditionalClassName({
                  "LpHero--heading h1": true,
                  "margin-bottom--S": !!useDisplayFont,
                  "margin-bottom--0": !bodyText?.bodyText && optionalCtas.length === 0,
                })}
                tagName="h1"
                title={title}
                useDisplayFont={!!useDisplayFont}
                useLargerFontSize={_variant === LP_HERO_VARIANTS.VERTICAL}
              />
            )}
            {bodyText?.bodyText && (
              <p className="LpHero--description">
                <Markdown>{bodyText?.bodyText}</Markdown>
              </p>
            )}

            {optionalCtas.length > 0 && (
              <div className="LpHero--cta-wrapper">
                {optionalCtas.map((cta, i) => {
                  if (
                    hasDemoSchedulerForm &&
                    cta.variant === LP_HERO_CTA_VARIANTS.FORM
                  )
                    return (
                      <DemoSchedulerForm />
                    )

                  if (
                    hasReferralForm &&
                    cta.variant === LP_HERO_CTA_VARIANTS.FORM
                  )
                    return (
                      <ReferralForm />
                    )

                  if (
                    hubspotFormData &&
                    cta.variant === LP_HERO_CTA_VARIANTS.FORM
                  )
                    return (
                      <HubspotForm
                        key={hubspotFormData.name}
                        {...hubspotFormData}
                        redirectUrl={formGatedPdfLink}
                      />
                    )

                  if (cta.variant === LP_HERO_CTA_VARIANTS.BUTTON)
                    return (
                      <Button
                        key={cta.label}
                        href={cta.url}
                        size={BUTTON_SIZES.L}
                        className="button"
                        onClick={cta.onClick}
                        variant={
                          i > 0
                            ? BUTTON_VARIANTS.SECONDARY
                            : BUTTON_VARIANTS.PRIMARY
                        }
                      >
                        {cta.label as string}
                      </Button>
                    )

                  return null
                })}
              </div>
            )}
          </div>

          {(image || wistiaId) && (
            <div
              className={generateConditionalClassName({
                "LpHero--media-wrapper column--1-7-XS column--1-13-M": true,
                "column--7-13-L":
                  _variant === LP_HERO_VARIANTS.FULL_BLEED ||
                  _variant === LP_HERO_VARIANTS.HORIZONTAL,
                "border-radius--M":
                  _variant !== LP_HERO_VARIANTS.FULL_BLEED &&
                  _variant !== LP_HERO_VARIANTS.VERTICAL,
                "border-radius--L":
                  _variant !== LP_HERO_VARIANTS.FULL_BLEED &&
                  _variant === LP_HERO_VARIANTS.VERTICAL,
                "border-radius--0": _variant === LP_HERO_VARIANTS.FULL_BLEED,
              })}
            >
              <Media
                isAboveFold
                excludeRoundedCorners={_variant === LP_HERO_VARIANTS.FULL_BLEED}
                maxWidth={
                  _variant === LP_HERO_VARIANTS.VERTICAL ?
                    1600 :
                    1040
                }
                {...{
                  image,
                  includeMediaBoxShadow,
                  playVideoInline,
                  playVideoInPnpPlayer,
                  wistiaId,
                }}
              />
            </div>
          )}
        </div>
      </div>
      {backgroundImage?.imgixImage?.gatsbyImageData && _variant === LP_HERO_VARIANTS.FULL_BLEED && (
        <div
          className="LpHero--background-image-wrapper"
          style={{
            aspectRatio: `${backgroundImage.imgixImage.gatsbyImageData.width / backgroundImage.imgixImage.gatsbyImageData.height}`,
          }}
        >
          <GatsbyImage
            alt=""
            className="LpHero--background-image"
            image={backgroundImage.imgixImage.gatsbyImageData}
            role="presentation"
          />
        </div>
      )}
      {openFormInModal && isFormModalOpen && hubspotFormData && (
        <Modal
          onClose={() => setIsFormModalOpen(false)}
        >
          <h2 className="type--headline-2XL margin-bottom--S">{button1Label}</h2>
          <HubspotForm
            key={hubspotFormData.name}
            {...hubspotFormData}
            redirectUrl={formGatedPdfLink}
          />
        </Modal>
      )}
    </Hero>
  )
}

export const LpHeroFragment = graphql`
  fragment LpHeroFragment on ContentfulLpHero {
    ...HeroMediaFragment
    ...HeroBackgroundImageFragment
    contentful_id
    bodyText {
      bodyText
    }
    backgroundColor
    button1Label
    button1Url
    button2Label
    button2Url
    form
    formGatedPdfLink
    hasSplitBackground
    hubspotFormData {
      ...HubspotFormFragment
    }
    includeBottomOverlap
    includeMediaBoxShadow
    marginBottom
    openFormInModal
    overline
    overlineIcon
    overlineLinkUrl
    title
    useDisplayFont
    variant
  }
`

export default LpHero
